import { applicationEditId } from '../../utils/applicationEditId';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Autocomplete, AutocompleteOption, ListItemContent, ListItemDecorator,
  Box, Button, Chip, CircularProgress, Divider, FormControl, FormHelperText, FormLabel,
  IconButton, Input, Modal, ModalDialog, ModalOverflow, Option, Select, Sheet,
  Stack, Textarea, Typography, Link
} from "@mui/joy";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import AssignmentIndRoundedIcon from '@mui/icons-material/AssignmentIndRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import ArchitectureRoundedIcon from '@mui/icons-material/ArchitectureRounded';
import FeedRoundedIcon from '@mui/icons-material/FeedRounded';
import ContactPhoneRoundedIcon from '@mui/icons-material/ContactPhoneRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import MyLocationRoundedIcon from "@mui/icons-material/MyLocationRounded";

import { LayoutContext, OBJECT_TYPES, DESIRED_TIME_OPTIONS } from "../../context";
import { API_URL, APIClientCreateApplication, APIClientConfirmApplicationEdit, APIClientDeleteApplicationFile, APIClientUpdateApplication, APIClientUploadApplicationFile, APIClientGetMyApplications, APIClientGetApplication, APISearchCities, APIClientCalculatorQuote, APIClientSaveDraft, APIClientFinalizeDraft, APIClientSuggestAddress, APIClientGeocodeAddress } from "../../api";
import { heicToJpeg, heicToJpegMany } from "../../shared/heic";
import MaskedInput from "../JoyMaskedInput";
import ApplicationScheduleFields from '../Schedule/ApplicationScheduleFields';
import { restoreSchedule, schedulePayload, scheduleErrors } from '../Schedule/schedule';
import { splitFullName, joinFullName, getDisplayName } from "../../shared/nameUtils";

import { usePermissions } from "../../usePermissions";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { APIReferralGetSettings, APIClientGetBaseSpec, APIClientDetectCity } from "../../api";
import { composeAddress, stripCityPrefix } from "../../shared/address";
import { createApplicationDraftPersistence, sameCityId, savedCityOption } from '../../shared/applicationDraft';
import RouteAttribution from '../RouteAttribution';


// Фикс для иконки маркера в webpack/vite
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});



const MAX_FILES = 100;
const MAX_FILE_SIZE_MB = 2048;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

function formatFileSize(bytes) {
  if (!bytes) return '0 Б';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

// Срезание города из адреса вынесено в shared/address.js — там же, где им
// пользуется карточка заявки. Локальная регулярка не понимала префиксы
// («г Пермь» при городе «Пермь» не срезался, и город уезжал на сервер дважды).
function stripCityFromAddress(address, cityName) {
  return stripCityPrefix(address, cityName);
}


// ─── Section ──────────────────────────────────────────────────────────────────
function Section({ title, icon, children, index = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
    >
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Sheet
            variant="solid" color="primary"
            sx={{
              width: 32, height: 32, borderRadius: "10px",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.9rem", flexShrink: 0,
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              boxShadow: "0 3px 10px rgba(99,102,241,0.3)",
            }}
          >
            {icon}
          </Sheet>
          <Typography level="title-sm" fontWeight={700}>{title}</Typography>
        </Stack>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 1.5 }}>
          {children}
        </Box>
      </Box>
    </motion.div>
  );
}

function FileDropZone({ files, setFiles, existingFiles = [], onRemoveExisting, disabled = false, onPreparingChange }) {
  const inputRef = useRef(null);
  const activeRef = useRef(true);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    activeRef.current = true;
    return () => { activeRef.current = false; };
  }, []);

  const totalCount = files.length + existingFiles.length;

  const addFiles = async (fileList) => {
    if (disabled) return;
    onPreparingChange?.(true);
    try {
      // HEIC/HEIF → JPEG до проверок (размер считаем уже по JPEG)
      const newFiles = await heicToJpegMany(fileList);
      if (!activeRef.current) return;
      const errors = [];

      const filtered = newFiles.filter(f => {
        if (f.size > MAX_FILE_SIZE) {
          errors.push(`${f.name} — превышает 2 ГиБ`);
          return false;
        }
        return true;
      });

      const available = MAX_FILES - totalCount;
      const toAdd = filtered.slice(0, Math.max(0, available));

      if (filtered.length > available) {
        errors.push(`Максимум ${MAX_FILES} файлов. Лишние не добавлены.`);
      }

      if (toAdd.length > 0) {
        setFiles(prev => [...prev, ...toAdd]);
      }

      if (errors.length) alert(errors.join('\n'));
    } catch (error) {
      alert(error.message || 'Не удалось подготовить файлы. Выберите их ещё раз.');
    } finally {
      if (activeRef.current) onPreparingChange?.(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const handleSelect = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const removeNew = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => { if (!disabled) inputRef.current?.click(); }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label="Выбрать вводные файлы"
        onKeyDown={event => {
          if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        sx={{
          border: '2px dashed',
          borderColor: dragOver ? 'primary.400' : 'neutral.300',
          borderRadius: '14px',
          p: 2.5,
          cursor: disabled || totalCount >= MAX_FILES ? 'not-allowed' : 'pointer',
          opacity: disabled || totalCount >= MAX_FILES ? 0.5 : 1,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
          transition: 'all 0.2s',
          background: dragOver ? 'var(--joy-palette-primary-50)' : 'transparent',
          '&:hover': totalCount < MAX_FILES ? {
            borderColor: 'primary.400',
            background: 'var(--joy-palette-primary-50)',
          } : {},
        }}
      >
        <AttachFileRoundedIcon sx={{ fontSize: 24, color: 'text.tertiary' }} />
        <Typography level="body-sm" fontWeight={600} color="neutral">
          Перетащите файлы или нажмите для выбора
        </Typography>
        <Typography level="body-xs" color="neutral">
          До {MAX_FILES} файлов, макс. 2 ГиБ каждый
        </Typography>
      </Box>
      <input
        ref={inputRef}
        type="file"
        disabled={disabled}
        multiple
        style={{ display: 'none' }}
        onChange={handleSelect}
      />

      {/* Существующие файлы (при редактировании) */}
      {existingFiles.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
          {existingFiles.map((ef) => (
            <Sheet
              key={ef.id}
              variant="outlined"
              sx={{
                borderRadius: '10px', px: 1.5, py: 1,
                display: 'flex', alignItems: 'center', gap: 1,
              }}
            >
              <InsertDriveFileRoundedIcon sx={{ fontSize: 18, color: 'primary.500' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography level="body-xs" fontWeight={600} noWrap>
                  {ef.original_name || 'Файл'}
                </Typography>
                <Typography level="body-xs" color="neutral">
                  {formatFileSize(ef.size)}
                </Typography>
              </Box>
              <IconButton
                size="sm" variant="plain" color="danger"
                onClick={() => onRemoveExisting(ef.id)}
                disabled={disabled}
                aria-label={`Убрать файл ${ef.original_name || ''}`}
                sx={{ minWidth: 28, minHeight: 28 }}
              >
                <CloseRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Sheet>
          ))}
        </Stack>
      )}

      {/* Новые файлы */}
      {files.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: existingFiles.length > 0 ? 1 : 1.5 }}>
          {files.map((f, idx) => (
            <motion.div
              key={`${f.name}-${idx}`}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Sheet
                variant="soft" color="primary"
                sx={{
                  borderRadius: '10px', px: 1.5, py: 1,
                  display: 'flex', alignItems: 'center', gap: 1,
                }}
              >
                {f.type?.startsWith('image/')
                  ? <ImageRoundedIcon sx={{ fontSize: 18, color: 'primary.600' }} />
                  : <InsertDriveFileRoundedIcon sx={{ fontSize: 18, color: 'primary.600' }} />
                }
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography level="body-xs" fontWeight={600} noWrap>{f.name}</Typography>
                  <Typography level="body-xs" color="neutral">
                    {formatFileSize(f.size)} — новый
                  </Typography>
                </Box>
                <IconButton
                  size="sm" variant="plain" color="danger"
                  onClick={() => removeNew(idx)}
                  disabled={disabled}
                  aria-label={`Убрать файл ${f.name}`}
                  sx={{ minWidth: 28, minHeight: 28 }}
                >
                  <CloseRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Sheet>
            </motion.div>
          ))}
        </Stack>
      )}

      {totalCount > 0 && (
        <Typography level="body-xs" color="neutral" sx={{ mt: 0.5, textAlign: 'right' }}>
          {totalCount} из {MAX_FILES} файлов
        </Typography>
      )}
    </Box>
  );
}

// ─── Мульти-календарь ─────────────────────────────────────────────────────────
function MultiDateCalendar({ selectedDates, onChange }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
  const MONTHS = [
    "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
    "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"
  ];

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  // getDay() → 0=Вс, нам нужно 0=Пн
  const firstDayOfWeek = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;

  const toDateStr = (y, m, d) => {
    const mm = String(m + 1).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
  };

  const isSelected = (d) => selectedDates.includes(toDateStr(viewYear, viewMonth, d));

  const isPast = (d) => {
    const date = new Date(viewYear, viewMonth, d);
    return date < today;
  };

  const isToday = (d) => {
    return viewYear === today.getFullYear()
      && viewMonth === today.getMonth()
      && d === today.getDate();
  };

  const isWeekend = (d) => {
    const day = new Date(viewYear, viewMonth, d).getDay();
    return day === 0 || day === 6;
  };

  const toggleDate = (d) => {
    if (isPast(d)) return;
    const str = toDateStr(viewYear, viewMonth, d);
    if (selectedDates.includes(str)) {
      onChange(selectedDates.filter(x => x !== str));
    } else {
      onChange([...selectedDates, str].sort());
    }
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  // Не позволяем уйти в прошлое
  const canGoPrev = viewYear > today.getFullYear()
    || (viewYear === today.getFullYear() && viewMonth > today.getMonth());

  const cells = [];
  // Пустые ячейки до первого дня
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <Sheet
      variant="outlined"
      sx={{
        borderRadius: "16px",
        overflow: "hidden",
        userSelect: "none",
      }}
    >
      {/* Шапка: навигация по месяцам */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5 }}
      >
        <IconButton
          size="sm" variant="plain"
          disabled={!canGoPrev}
          onClick={prevMonth}
          sx={{ borderRadius: "50%" }}
        >
          <Typography sx={{ fontSize: 18 }}>←</Typography>
        </IconButton>

        <Typography level="title-sm" fontWeight={700}>
          {MONTHS[viewMonth]} {viewYear}
        </Typography>

        <IconButton
          size="sm" variant="plain"
          onClick={nextMonth}
          sx={{ borderRadius: "50%" }}
        >
          <Typography sx={{ fontSize: 18 }}>→</Typography>
        </IconButton>
      </Stack>

      {/* Дни недели */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          px: 1, pb: 0.5,
        }}
      >
        {WEEKDAYS.map((wd, i) => (
          <Typography
            key={wd}
            level="body-xs"
            fontWeight={600}
            sx={{
              textAlign: "center",
              color: i >= 5 ? "danger.500" : "text.tertiary",
            }}
          >
            {wd}
          </Typography>
        ))}
      </Box>

      {/* Дни */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "2px",
          px: 1, pb: 1.5,
        }}
      >
        {cells.map((d, i) =>
          d === null ? (
            <Box key={`empty-${i}`} />
          ) : (
            <Box
              key={d}
              onClick={() => toggleDate(d)}
              sx={{
                aspectRatio: "1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "10px",
                cursor: isPast(d) ? "default" : "pointer",
                opacity: isPast(d) ? 0.3 : 1,
                fontWeight: isSelected(d) || isToday(d) ? 700 : 400,
                fontSize: "0.85rem",
                color: isSelected(d)
                  ? "#fff"
                  : isWeekend(d)
                    ? "var(--joy-palette-danger-500)"
                    : "inherit",
                background: isSelected(d)
                  ? "linear-gradient(135deg, #6366f1, #8b5cf6)"
                  : isToday(d)
                    ? "var(--joy-palette-neutral-100)"
                    : "transparent",
                boxShadow: isSelected(d) ? "0 2px 8px rgba(99,102,241,0.35)" : "none",
                transition: "all 0.15s",
                "&:hover": !isPast(d) && !isSelected(d)
                  ? { background: "var(--joy-palette-primary-50)" }
                  : {},
              }}
            >
              {d}
            </Box>
          )
        )}
      </Box>
    </Sheet>
  );
}

// ─── Leaflet-карта с геокодингом ──────────────────────────────────────────────

// Вспомогательный компонент для обработки кликов по карте
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng);
    },
  });
  return null;
}

// Вспомогательный компонент для перемещения карты программно
function MapFlyTo({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, 16, { duration: 0.8 });
    }
  }, [center]);
  return null;
}

function AddressMap({ address, cityName, lat, lng, onLocationChange, onAddressChange, disabled, addressFocused }) {
  const [marker, setMarker] = useState(lat && lng ? [lat, lng] : null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const geocodeTimer = useRef(null);
  const selectedAddressRef = useRef(null); // выбранный адрес не ищем повторно до изменения текста
  const suggestionsRef = useRef(null);

  useEffect(() => {
    if (lat && lng) setMarker([lat, lng]);
  }, [lat, lng]);

  // Закрыть подсказки при клике вне
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Forward-поиск адреса через бэкенд (DaData, с учётом города).
  // Раньше шёл в Nominatim (OSM): по российским адресам он часто не находит дом,
  // из-за чего подсказки не появлялись и адрес нельзя было подтвердить.
  useEffect(() => {
    if (!addressFocused || disabled || !address || address.trim().length < 3) {
      setShowSuggestions(false);
      setSuggestions([]);
      return;
    }
    // Если адрес подставлен из карты/подсказки — не запускаем повторный поиск
    if (selectedAddressRef.current === address) return;
    let cancelled = false;
    clearTimeout(geocodeTimer.current);
    geocodeTimer.current = setTimeout(async () => {
      try {
        const res = await APIClientSuggestAddress(address, cityName);
        if (cancelled) return;
        const list = Array.isArray(res?.suggestions) ? res.suggestions : [];
        if (list.length > 0) {
          setSuggestions(list);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (err) {
        console.error("Suggest error:", err);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(geocodeTimer.current); };
  }, [address, cityName, disabled, addressFocused]);

  // Пользователь выбрал подсказку
  const selectSuggestion = (item) => {
    const newLat = item.latitude != null ? parseFloat(item.latitude) : null;
    const newLng = item.longitude != null ? parseFloat(item.longitude) : null;
    // value — полный адрес DaData («г Ростов-на-Дону, ул Ленина, д 42»);
    // убираем город из начала, чтобы в поле осталась адресная часть.
    const shortAddress = stripCityFromAddress(item.value || "", cityName);

    selectedAddressRef.current = shortAddress;
    onAddressChange(shortAddress);
    if (newLat != null && newLng != null) {
      setMarker([newLat, newLng]);
      setFlyTarget([newLat, newLng]);
      onLocationChange(newLat, newLng);
    }
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Форматирование подсказки для отображения
  const formatSuggestion = (item) => {
    const full = String(item.value || "");
    const city = item.city || "";
    // В основную строку — адрес без города, в подпись — город.
    const main = stripCityFromAddress(full, city) || full;
    return { main, sub: city };
  };

  // Клик по карте → reverse geocode
  const handleMapClick = async (latlng) => {
    if (disabled) return;
    setMarker([latlng.lat, latlng.lng]);
    onLocationChange(latlng.lat, latlng.lng);

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?` +
        `format=json&lat=${latlng.lat}&lon=${latlng.lng}&accept-language=ru&addressdetails=1`
      );
      const data = await res.json();
      if (data.address) {
        const a = data.address;
        const parts = [
          a.road,
          a.house_number && `д. ${a.house_number}`,
          a.building && `стр. ${a.building}`,
        ].filter(Boolean);
        const cleaned = stripCityFromAddress(parts.join(", ") || data.display_name, cityName);
        selectedAddressRef.current = cleaned;
        onAddressChange(cleaned);
      }
    } catch (err) {
      console.error("Reverse geocoding error:", err);
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Выпадающий список подсказок */}
      {addressFocused && showSuggestions && suggestions.length > 0 && (
        <Sheet
          ref={suggestionsRef}
          variant="outlined"
          sx={{
            position: "absolute",
            top: -4,
            left: 0,
            right: 0,
            zIndex: 1200,
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            maxHeight: 220,
            overflowY: "auto",
          }}
        >
          {suggestions.map((item, idx) => {
            const { main, sub } = formatSuggestion(item);
            return (
              <Box
                key={idx}
                onMouseDown={e => e.preventDefault()}
                onClick={() => selectSuggestion(item)}
                sx={{
                  px: 2, py: 1.2,
                  cursor: "pointer",
                  borderBottom: idx < suggestions.length - 1 ? "1px solid" : "none",
                  borderColor: "divider",
                  transition: "background 0.15s",
                  "&:hover": { background: "var(--joy-palette-primary-50)" },
                }}
              >
                <Typography level="body-sm" fontWeight={600}>{main}</Typography>
                {sub && (
                  <Typography level="body-xs" color="neutral">{sub}</Typography>
                )}
              </Box>
            );
          })}
        </Sheet>
      )}

      {/* Карта */}
      <Box
        sx={{
          borderRadius: "14px",
          overflow: "hidden",
          border: "1.5px solid",
          borderColor: disabled ? "warning.300" : "neutral.200",
          height: 260,
          mt: 1,
          position: "relative",
          "& .leaflet-container": {
            height: "100%",
            width: "100%",
            borderRadius: "12px",
          },
        }}
      >
        {disabled && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.7)",
              backdropFilter: "blur(2px)",
              cursor: "not-allowed",
            }}
          >
            <Typography level="body-sm" fontWeight={600} color="warning">
              Сначала выберите город
            </Typography>
          </Box>
        )}

        <MapContainer
          center={marker || [56.25, 43.45]}
          zoom={marker ? 16 : 5}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://osm.org/copyright">OSM</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onMapClick={handleMapClick} />
          {flyTarget && <MapFlyTo center={flyTarget} />}
          {marker && <Marker position={marker} />}
        </MapContainer>
      </Box>
    </Box>
  );
}


// Сентинел для пункта "Моего города нет"
const CUSTOM_CITY_OPTION = { id: '__CUSTOM__', name: '✏️ Моего города нет в списке' };

// Фабрика опции inline-добавления (пункт с текущим введённым значением)
const makeAddNewCityOption = (cityName) => ({
    id: '__NEW__',
    name: cityName,
    isNew: true,
});

function CityAutocomplete({ value, cityName, knownCities = [], customCityName, onChange, onCustomChange, error }) {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [myCities, setMyCities] = useState([]);
  const [myCitiesLoaded, setMyCitiesLoaded] = useState(false);
  const [myCitiesError, setMyCitiesError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showCustomInput, setShowCustomInput] = useState(!!customCityName);
  const searchTimer = useRef(null);
  const MAX_RETRIES = 3;

  // Загрузка "моих городов" из прошлых заявок
  const loadMyCities = useCallback(async () => {
    setLoading(true);
    setMyCitiesError(false);
    try {
      const apps = await APIClientGetMyApplications();
      if (Array.isArray(apps) && apps.length > 0) {
        const cityMap = new Map();
        apps.forEach(app => {
          if (app.city && app.city_name) {
            cityMap.set(app.city, { id: app.city, name: app.city_name });
          }
        });
        const cities = Array.from(cityMap.values());
        setMyCities(cities);
        setMyCitiesLoaded(true);
        setLoading(false);
        return true;
      } else {
        const random = await APISearchCities('', 10);
        setMyCities(random);
        setMyCitiesLoaded(true);
        setLoading(false);
        return true;
      }
    } catch (_) {
      setLoading(false);
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    const tryLoad = async () => {
      while (attempt < MAX_RETRIES && !cancelled) {
        const success = await loadMyCities();
        if (success || cancelled) return;
        attempt++;
        setRetryCount(attempt);
        if (attempt < MAX_RETRIES) {
          await new Promise(r => setTimeout(r, 2000 * attempt));
        }
      }
      if (!cancelled && attempt >= MAX_RETRIES) {
        setMyCitiesError(true);
      }
    };
    tryLoad();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setOptions([]);
      return;
    }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setLoading(true);
      const results = await APISearchCities(inputValue, 15);
      setOptions(results);
      setLoading(false);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [inputValue]);

  const handleManualRetry = async () => {
    setMyCitiesError(false);
    setRetryCount(0);
    const success = await loadMyCities();
    if (!success) setMyCitiesError(true);
  };

  // Формируем список опций + добавляем "Моего города нет"
  const displayOptions = useMemo(() => {
    const query = inputValue?.trim() || '';
    const hasQuery = query.length >= 2;

    let result = [];
    if (hasQuery && options.length > 0) {
        result = options;
    } else if (!hasQuery && myCities.length > 0) {
        result = myCities;
    }

    // ── НОВОЕ: если пользователь что-то ввёл, но точного совпадения нет —
    //          предлагаем добавить как новый город прямо в списке.
    if (hasQuery) {
        const exactMatch = result.some(c =>
            (c?.name || '').toLowerCase().trim() === query.toLowerCase()
        );
        if (!exactMatch) {
            result = [...result, makeAddNewCityOption(query)];
        }
    }

    // Старая опция "Моего города нет" — оставляем только когда пользователь
    // НЕ ввёл текст (если ввёл — у него уже есть более конкретный `__NEW__`).
    if (!hasQuery) {
        result = [...result, CUSTOM_CITY_OPTION];
    }

    const restored = savedCityOption(value, cityName, knownCities);
    if (restored && !result.some(city => sameCityId(city.id, restored.id))
        && (!hasQuery || restored.name.toLowerCase().includes(query.toLowerCase()))) {
      result = [restored, ...result];
    }
    return result;
  }, [options, myCities, inputValue, value, cityName, knownCities]);

  // Текущий выбранный объект
  const selectedCity = (() => {
    if (showCustomInput) return CUSTOM_CITY_OPTION;
    // ── НОВОЕ: если custom_city_name заполнен, но city.id не выбран —
    //          значит выбран inline-вариант "__NEW__"
    if (!value && customCityName) {
        return makeAddNewCityOption(customCityName);
    }
    if (!value) return null;
    return (
        savedCityOption(value, cityName, [...displayOptions, ...myCities, ...knownCities])
    );
  })();

  useEffect(() => {
    if (value) setShowCustomInput(false);
    if (selectedCity) setInputValue(selectedCity.name);
  }, [value, selectedCity?.id, selectedCity?.name]);

  return (
    <Box>
      <Autocomplete
        placeholder="Введите город или выберите из списка"
        value={selectedCity}
        onChange={(_, newVal) => {
          if (!newVal) {
              setInputValue('');
              setShowCustomInput(false);
              onChange(null);
              onCustomChange?.('');
              return;
          }
          if (newVal.id === '__CUSTOM__') {
              setShowCustomInput(true);
              onChange(null);
              return;
          }
          if (newVal.id === '__NEW__') {
              setShowCustomInput(false);  // отдельное поле НЕ нужно — имя уже введено
              onChange(null);              // сбрасываем city.id (он не из справочника)
              onCustomChange?.(newVal.name);  // сохраняем введённое имя в custom_city_name
              return;
          }
          // Выбран обычный город из справочника
          setShowCustomInput(false);
          onCustomChange?.('');
          setInputValue(newVal.name);
          onChange(newVal.id, newVal);
        }}
        inputValue={inputValue}
        onInputChange={(_, val) => setInputValue(val)}
        options={displayOptions}
        getOptionLabel={(opt) => opt?.name || ''}
        isOptionEqualToValue={(opt, val) => sameCityId(opt?.id, val?.id)}
        loading={loading}
        noOptionsText={inputValue ? "Загрузка..." : "Начните вводить название"}
        startDecorator={<SearchRoundedIcon sx={{ fontSize: 18, color: 'text.tertiary' }} />}
        endDecorator={loading ? <CircularProgress size="sm" sx={{ mr: 1 }} /> : null}
        slotProps={{
          listbox: { sx: { maxHeight: 240 } },
        }}
        renderOption={(props, option) => {
          const isCustomMarker = option.id === '__CUSTOM__';
          const isNewInline    = option.id === '__NEW__';

          let sx = {};
          if (isCustomMarker) {
              sx = {
                  borderTop: '1px solid', borderColor: 'divider',
                  fontStyle: 'italic',
                  '& *': { color: 'primary.500', fontWeight: 600 },
              };
          } else if (isNewInline) {
              sx = {
                  borderTop: '1px solid', borderColor: 'divider',
                  bgcolor: 'primary.50',
                  '&:hover': { bgcolor: 'primary.100' },
              };
          }

          return (
              <AutocompleteOption {...props} key={option.id} sx={sx}>
                  <ListItemContent>
                      {isNewInline ? (
                          <Box>
                              <Typography level="body-sm" sx={{ fontWeight: 700, color: 'primary.700' }}>
                                  + Использовать «{option.name}» как новый город
                              </Typography>
                              <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.25 }}>
                                  💡 Будет проверен менеджером
                              </Typography>
                          </Box>
                      ) : (
                          <Typography level="body-sm" fontWeight={600}>{option.name}</Typography>
                      )}
                  </ListItemContent>
              </AutocompleteOption>
          );
        }}
        sx={{
          '& .MuiAutocomplete-input': { fontSize: '0.875rem' },
          borderRadius: "lg"
        }}
        color={error ? "danger" : "neutral"}
        error={!!error}
      />

      {!value && customCityName && !showCustomInput && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
            <Chip
                size="sm"
                variant="soft"
                color="primary"
                startDecorator={<span>🆕</span>}
            >
                Новый город: {customCityName}
            </Chip>
            <Button
                variant="plain"
                color="neutral"
                size="sm"
                onClick={() => {
                    onCustomChange?.('');
                    onChange(null);
                    setInputValue('');
                }}
                sx={{ minHeight: 'auto', px: 0.5, py: 0.25 }}
            >
                Сбросить
            </Button>
        </Stack>
      )}

      {/* Инпут для ввода нового города */}
      <AnimatePresence>
        {showCustomInput && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Box sx={{ mt: 1.5 }}>
              <Input
                size="lg"
                placeholder="Введите название города"
                value={customCityName || ''}
                onChange={(e) => onCustomChange?.(e.target.value)}
                startDecorator={<EditRoundedIcon sx={{ fontSize: 18, color: 'primary.500' }} />}
                sx={{ borderRadius: 'lg' }}
              />
              <Typography level="body-xs" color="warning" sx={{ mt: 0.5 }}>
                💡 Город будет добавлен в базу автоматически после проверки менеджером
              </Typography>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {!inputValue && myCitiesLoaded && myCities.length > 0 && !showCustomInput && (
        <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
          <HistoryRoundedIcon sx={{ fontSize: 12, mr: 0.3, verticalAlign: 'middle' }} />
          Ваши города из прошлых заявок. Для поиска начните вводить название
        </Typography>
      )}

      {myCitiesError && (
        <Button
          variant="soft" color="warning" size="sm"
          startDecorator={<RefreshRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={handleManualRetry}
          sx={{ mt: 1, borderRadius: '10px' }}
        >
          Загрузить мои города
        </Button>
      )}
    </Box>
  );
}


// ─── OrderModal ───────────────────────────────────────────────────────────────
export default function OrderModal({ open, onClose, editOrder = null }) {
  const { account, programs, citys } = useContext(LayoutContext);
  // const isEdit = editOrder !== null && editOrder !== undefined && !!editOrder?.id;

  const isDraft = editOrder?.status === 'draft';          // продолжаем черновик
  const isEdit = !!editOrder && !isDraft;                  // редактируем реальную заявку
  const requiredPerm = isEdit ? 'edit_application' : 'create_application';

  const { hasPermission } = usePermissions();
  
  const [submitted, setSubmitted] = useState(false);
  const [submittedPending, setSubmittedPending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState({});
  const [draftId, setDraftId] = useState(null);
  const [draftState, setDraftState] = useState({ status: 'idle', at: null });   // idle | saving | saved | error
  const draftIdRef = useRef(null);
  const submittedApplicationRef = useRef(null);
  const submitFlightRef = useRef(false);
  const editSaveRef = useRef({ id: null, phase: 'editing', files: new WeakMap() });
  const draftSaveRef = useRef(null);
  const draftPersistenceRef = useRef(null);
  const formRef = useRef(null);
  const scheduleTouchedRef = useRef(false);
  const closeFlightRef = useRef(false);
  const [closing, setClosing] = useState(false);
  const draftSessionRef = useRef(0);
  const scrollRef = useRef(null);
  const [referralPreview, setReferralPreview] = useState(null);
  const [estimate, setEstimate] = useState({ total: null, minimumApplied: false, loading: false });
  const [availability, setAvailability] = useState({ data: null, loading: false });
  const [quoteRevision, setQuoteRevision] = useState(0);
  const sessionToken = localStorage.getItem('client_token') || '';
  const quoteScope = `${account?.id || ''}:${sessionToken}`;
  const quoteScopeRef = useRef(quoteScope); quoteScopeRef.current = quoteScope;
  const previousDraftOwner = useRef(quoteScope), draftOwnerEpoch = useRef(0), activeDraftOwner = useRef('');
  if (previousDraftOwner.current !== quoteScope) { previousDraftOwner.current = quoteScope; draftOwnerEpoch.current += 1; }
  activeDraftOwner.current = `${quoteScope}:${draftOwnerEpoch.current}`;
  const visibleDraftOwner = useRef(quoteScope);
  useEffect(() => {
    if (visibleDraftOwner.current === quoteScope) return;
    visibleDraftOwner.current = quoteScope;
    if (open) onClose?.();
  }, [quoteScope, open, onClose]);
  const [detecting, setDetecting] = useState(false);
  const [detectMsg, setDetectMsg] = useState(null);
  const [addressFocused, setAddressFocused] = useState(false);

  // Базовое ТЗ (что входит в обмер) — для подсказки у поля ТЗ
  const [baseSpec, setBaseSpec] = useState(null);

  const buildInitialForm = () => {
    // Берём ФИО из editOrder или account; парсим full_name как fallback
    const src = editOrder || account || {};
    const split = splitFullName(src.full_name);
    return {
      client_last_name:  src.last_name  ?? split.last_name,
      client_name:       src.name       ?? split.name,
      client_patronymic: src.patronymic ?? split.patronymic,
      client_phone:   editOrder?.phone        || account?.phone     || "",
      city:               editOrder?.city              || null,
      city_name:          editOrder?.city_name         || '',
      custom_city_name:   editOrder?.custom_city_name  || "",
      address:        editOrder?.address || "",
      latitude:       editOrder?.latitude ?? null,
      longitude:      editOrder?.longitude ?? null,
      object_type:    editOrder?.object_type  || "",
      area:           editOrder?.square ?? "",
      programs:       editOrder?.programs     || [],
      plan_file:      null,
      existing_plan_file: editOrder?.plan_file || null,
      tz:             editOrder?.tz           || "",
      comment:        editOrder?.comment      || "",
      desired_time:   editOrder?.desired_time || "",
      desired_date:   editOrder?.desired_date || "",
      ...restoreSchedule(editOrder || {}),
      contact_name:   editOrder?.contact_name || "",
      contact_phone:  editOrder?.contact_phone|| "",
      contact_comment:editOrder?.contact_comment || "",
      input_files: [],
      files_preparing: false,
      plan_preparing: false,
      existing_files: editOrder?.input_files || [],
      removed_file_ids: [],
    }
  };

  const [form, setForm] = useState(buildInitialForm);
  formRef.current = form;
  const selectedFormCity = savedCityOption(form.city, form.city_name, citys);

  // Когда открываем модалку для другой заявки — пересоздаём форму
  useEffect(() => {
    draftSessionRef.current += 1;
    scheduleTouchedRef.current = false;
    const ownerScope = activeDraftOwner.current, pinnedToken = sessionToken;
    const assertCurrent = () => {
      if (activeDraftOwner.current !== ownerScope || (localStorage.getItem('client_token') || '') !== pinnedToken) {
        throw new DOMException('Аккаунт изменился. Откройте заявку заново.', 'AbortError');
      }
    };
    draftIdRef.current = editOrder?.status === 'draft' ? editOrder.id : null;
    draftSaveRef.current = null;
    draftPersistenceRef.current = createApplicationDraftPersistence({
      initialId: editOrder?.status === 'draft' ? editOrder.id : null,
      saveDraft: payload => { assertCurrent(); return APIClientSaveDraft(payload, {sessionToken: pinnedToken}); },
      uploadFile: (appId, file, initial, editId, uploadId) => { assertCurrent(); return APIClientUploadApplicationFile(appId, file, initial, editId, uploadId, {sessionToken: pinnedToken}); },
      deleteFile: (appId, fileId) => { assertCurrent(); return APIClientDeleteApplicationFile(appId, fileId, null, {sessionToken: pinnedToken}); },
      makeId: applicationEditId,
      assertCurrent,
    });
    closeFlightRef.current = false;
    setClosing(false);
    submittedApplicationRef.current = null;
    editSaveRef.current = { id: null, phase: 'editing', files: new WeakMap() };
    setSubmitting(false);
    submitFlightRef.current = false;
    if (open) {
      setAddressFocused(false);
      setForm(buildInitialForm());
      setSubmitted(false);
      setSubmitError("");
      setErrors({});
      setDraftId(editOrder?.status === 'draft' ? editOrder.id : null);
      setDraftState({ status: 'idle', at: null });
    }
  }, [open, editOrder, account?.id, sessionToken]);

  // Правка реальной заявки: гарантированно подтягиваем полную заявку деталью и
  // заполняем ПУСТЫЕ поля формы (если в списке заявка пришла «тонкой» — без контактов).
  // Уже введённые/пришедшие значения не затираем. Черновики сюда не попадают (isEdit=false)
  // — они и так приходят полными из APIClientGetDraft.
  useEffect(() => {
    if (!open || !isEdit || !editOrder?.id) return;
    let cancelled = false;
    (async () => {
      const full = await APIClientGetApplication(editOrder.id);
      if (cancelled || !full || full.status === 'error' || !full.id) return;
      setForm(prev => ({
        ...prev,
        ...(!scheduleTouchedRef.current ? {desired_time: full.desired_time || '', desired_date: full.desired_date || '', ...restoreSchedule(full)} : {}),
        contact_name:      prev.contact_name      || full.contact_name      || "",
        contact_phone:     prev.contact_phone     || full.contact_phone     || "",
        contact_comment:   prev.contact_comment   || full.contact_comment   || "",
        client_name:       prev.client_name       || full.name              || "",
        client_last_name:  prev.client_last_name  || full.last_name         || "",
        client_patronymic: prev.client_patronymic || full.patronymic        || "",
        client_phone:      prev.client_phone      || full.phone             || "",
        city:              prev.city              || full.city              || null,
        city_name:         prev.city_name         || full.city_name         || '',
        address:           prev.address           || full.address           || "",
        object_type:       prev.object_type       || full.object_type       || "",
        tz:                prev.tz                || full.tz                 || "",
        comment:           prev.comment           || full.comment           || "",
        existing_plan_file: prev.existing_plan_file || full.plan_file || null,
        existing_files:    (prev.existing_files && prev.existing_files.length)
                              ? prev.existing_files
                              : (full.input_files || []),
      }));
    })();
    return () => { cancelled = true; };
  }, [open, isEdit, editOrder?.id]);

  // A single authenticated snapshot supplies the company tariff and road
  // availability. Independent parallel quotes used to race and duplicate roads.
  useEffect(() => {
    if (!open) return undefined;
    const wake = () => { if (document.visibilityState === 'visible') setQuoteRevision(value => value + 1); };
    const storage = event => { if (event.key === 'client_token') wake(); };
    window.addEventListener('focus', wake); document.addEventListener('visibilitychange', wake);
    window.addEventListener('storage', storage);
    const timer = setInterval(wake, 60000);
    return () => { clearInterval(timer); window.removeEventListener('focus', wake);
      document.removeEventListener('visibilitychange', wake); window.removeEventListener('storage', storage); };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const square = parseInt(form.area, 10) || 0;
    if (square <= 0 && !form.city && !form.custom_city_name?.trim()) {
      setEstimate({total: null, loading: false}); setAvailability({data: null, loading: false}); return undefined;
    }
    let cancelled = false;
    const scope = quoteScope;
    setEstimate({total: null, minimumApplied: false, loading: square > 0});
    setAvailability({data: null, loading: true});
    const timer = setTimeout(async () => {
      const res = await APIClientCalculatorQuote({square, city_id: form.city || undefined,
        custom_city_name: form.custom_city_name?.trim() || undefined,
        lat: form.latitude ?? undefined, lng: form.longitude ?? undefined, with_availability: true}, {sessionToken});
      if (cancelled || quoteScopeRef.current !== scope || (localStorage.getItem('client_token') || '') !== sessionToken) return;
      if (res) {
        setEstimate({total: square > 0 ? (typeof res.client_total === 'number' ? res.client_total : res.transfer_pending ? res.services_total : null) : null,
          minimumApplied: !!res.minimum_applied, incomplete: !!res.transfer_pending, loading: false,
          tariffName: res.pricing?.tariff_name || '', route: res.transfer_route || null});
        setAvailability({data: res.availability || null, loading: false});
      } else {
        setEstimate({total: null, loading: false, error: square > 0 ? 'Не удалось получить стоимость. Повторите расчёт.' : ''});
        setAvailability({data: null, loading: false});
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.area, form.city, form.custom_city_name, form.latitude, form.longitude, open, account?.id, sessionToken, quoteRevision]);

  useEffect(() => {
    if (!open || isEdit) return;
    let cancelled = false;
    const scope = quoteScope;
    setReferralPreview(null);
    if (estimate.loading || estimate.incomplete || !Number.isFinite(estimate.total) || estimate.total <= 0) return;
    
    // Проверяем: был ли применён код (через API stats или localStorage)
    const pendingCode = localStorage.getItem('pending_referral_code');
    
    // Загружаем настройки + статистику моих использований
    Promise.all([
        APIReferralGetSettings(),
        APIClientGetMyApplications(),
    ]).then(([settings, myApps]) => {
        if (cancelled || quoteScopeRef.current !== scope) return;
        if (!settings?.is_active) return;
        
        // Если у меня уже есть заявки — скидку не дадут (один код = одна заявка)
        const hasApplications = Array.isArray(myApps) && myApps.length > 0;
        if (hasApplications) return;
        
        // Сумма из калькулятора, если есть (поле calculatorAmount или form.calculator_amount)
        const amount = Number(estimate.total);
        if (amount <= 0) return;
        
        // Считаем превью: percent × amount с min/max
        const raw = amount * settings.invitee.percent / 100;
        let estimated = raw;
        if (estimated < settings.invitee.min_amount) estimated = settings.invitee.min_amount;
        if (estimated > settings.invitee.max_amount) estimated = settings.invitee.max_amount;
        
        // Показываем превью только если код в pending или мы видим, что инвайт существует
        // (бэк проверит наличие инвайта по invitee=client; точно мы не знаем — показываем оптимистично)
        if (pendingCode || hasApplications === false) {
            setReferralPreview({
                will_apply: true,
                estimated_discount: estimated,
                percent: settings.invitee.percent,
                min_amount: settings.invitee.min_amount,
                max_amount: settings.invitee.max_amount,
            });
        }
    });
    return () => { cancelled = true; };
  }, [open, isEdit, estimate.total, estimate.incomplete, estimate.loading, account?.id, sessionToken]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    APIClientGetBaseSpec().then(s => { if (!cancelled) setBaseSpec(s); });
    return () => { cancelled = true; };
  }, [open]);

  // ── Черновики: полезная нагрузка, автосохранение (пункт 4) ──
  useEffect(() => { draftIdRef.current = draftId; }, [draftId]);

  const draftMeaningful = (snapshot = form) =>
    !!(snapshot.client_phone?.trim() || snapshot.client_name?.trim() || snapshot.client_last_name?.trim()
       || snapshot.address?.trim() || snapshot.area || snapshot.city || snapshot.custom_city_name?.trim()
       || snapshot.object_type || snapshot.comment?.trim() || snapshot.programs?.length
       || snapshot.input_files?.length || snapshot.plan_file);

  const persistDraft = (snapshot) => {
    const persistence = draftPersistenceRef.current;
    const session = draftSessionRef.current;
    if (!persistence) return Promise.reject(new Error('Черновик ещё не готов к сохранению'));
    const current = () => session === draftSessionRef.current;
    setDraftState(state => ({ ...state, status: 'saving' }));
    const saving = persistence.save(snapshot, {
      onMetadata: ({ id, application }) => {
        if (!current()) return;
        draftIdRef.current = id;
        setDraftId(id);
        if (application?.plan_file) {
          setForm(previous => previous.existing_plan_file === application.plan_file ? previous
            : { ...previous, existing_plan_file: application.plan_file });
        }
      },
      onFile: (file, receipt) => {
        if (!current()) return;
        setForm(previous => {
          const pending = previous.input_files.includes(file);
          const present = previous.existing_files.some(item => sameCityId(item.id, receipt.id));
          if (!pending && present) return previous;
          return { ...previous,
            input_files: previous.input_files.filter(item => item !== file),
            existing_files: [...previous.existing_files.filter(item => !sameCityId(item.id, receipt.id)), receipt],
          };
        });
      },
      onDelete: fileId => {
        if (!current()) return;
        setForm(previous => previous.removed_file_ids.some(id => sameCityId(id, fileId))
          ? { ...previous, removed_file_ids: previous.removed_file_ids.filter(id => !sameCityId(id, fileId)) }
          : previous);
      },
    }).then(result => {
      if (current() && draftSaveRef.current === saving && persistence.isPersisted(formRef.current)) {
        setDraftState({ status: 'saved', at: new Date() });
        setSubmitError('');
      }
      return result;
    }, error => {
      if (current() && draftSaveRef.current === saving) {
        setDraftState({ status: 'error', at: null });
        setSubmitError(error.message || 'Не удалось сохранить черновик');
      }
      throw error;
    });
    draftSaveRef.current = saving;
    const clear = () => { if (draftSaveRef.current === saving) draftSaveRef.current = null; };
    saving.then(clear, clear);
    return saving;
  };

  // Metadata, plan and input files share one serialized queue. 'Saved' appears
  // only after all attachments in that snapshot have durable server receipts.
  useEffect(() => {
    if (!open || isEdit || submitted || submitting || closing || submittedApplicationRef.current || !draftMeaningful()) return;
    setDraftState(state => ({ ...state, status: 'saving' }));
    const timer = setTimeout(() => { persistDraft(form).catch(() => undefined); }, 1500);
    return () => clearTimeout(timer);
  }, [form, open, isEdit, submitted, submitting, closing]);

  // Browsers cannot guarantee a large upload after a tab is closed. Explicit
  // modal close flushes the queue; leaving the tab while it is pending warns.
  useEffect(() => {
    if (!open || submitted) return;
    const beforeUnload = event => {
      const snapshot = formRef.current;
      if (snapshot?.input_files?.length || snapshot?.files_preparing || snapshot?.plan_preparing || draftSaveRef.current || ['saving', 'error'].includes(draftState.status)) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [open, submitted, draftState.status]);

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: "" })); };
  const setSchedule = value => { scheduleTouchedRef.current = true; setForm(previous => ({...previous, ...value})); setErrors(previous => ({...previous, desired_slots: ''})); };

  const validate = () => {
    const e = {};
    if (!form.client_last_name.trim()) e.client_last_name = "Укажите фамилию";
    if (!form.client_name.trim())      e.client_name      = "Укажите имя";
    if (!form.client_phone.trim()) e.client_phone = "Обязательное поле";
    if (!form.city && !form.custom_city_name.trim()) {
      e.city = "Выберите город или укажите свой";
    }
    // Координаты жёстко не требуем: адрес подтверждает сервер (DaData, с учётом города).
    // Если пользователь не выбрал подсказку/точку — координаты доберём при отправке
    // (геокод на сервере), а полностью нераспознанный адрес отсеет бэкенд (400).
    if (!form.address.trim()) e.address = "Укажите адрес объекта";
    if (!form.object_type) e.object_type = "Выберите тип объекта";
    if (!form.area) e.area = "Укажите площадь";
    if (!form.programs || form.programs.length === 0) e.programs = "Выберите хотя бы одну программу";
    // ── НОВОЕ: обязательные поля контакта ──
    if (!form.contact_name.trim()) e.contact_name = "Укажите имя встречающего";
    if (!form.contact_phone.trim()) e.contact_phone = "Укажите телефон встречающего";
    const scheduleError = scheduleErrors(form);
    if (scheduleError) e.desired_slots = scheduleError;
    return e;
  };

  const handleRemoveExistingFile = (fileId) => {
    setForm(prev => ({
        ...prev,
        existing_files: prev.existing_files.filter(f => f.id !== fileId),
        removed_file_ids: [...prev.removed_file_ids, fileId],
    }));
  };

  const handleSubmit = async () => {
    if (submitFlightRef.current) return;
    const saveScope = draftSessionRef.current;
    const ensureCurrent = () => { if (saveScope !== draftSessionRef.current) throw new Error('Форма закрыта или открыта другая заявка'); };
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    submitFlightRef.current = true;
    setSubmitting(true);
    setSubmitError("");
    // Автосейв мог ещё создавать черновик. Финализируем именно его, без дубля.
    try {
      if (draftSaveRef.current) await draftSaveRef.current;
      ensureCurrent();
    } catch (error) {
      if (saveScope === draftSessionRef.current) {
        setSubmitError(error.message || 'Не удалось сохранить вложения черновика. Повторите отправку.');
        setSubmitting(false);
        submitFlightRef.current = false;
      }
      return;
    }
    const submitDraftId = draftIdRef.current;

    // Подтверждение адреса на сервере (DaData, с учётом города): если пользователь не выбрал
    // подсказку и не ставил точку — пробуем получить координаты сами, чтобы точка ушла в сделку
    // и для прямого создания. На неточности НЕ блокируем: город указан, бэкенд мягко подтвердит
    // адрес; полностью нераспознанный адрес отсеет бэкенд.
    let resolvedLat = form.latitude;
    let resolvedLng = form.longitude;
    if ((resolvedLat == null || resolvedLng == null) && form.address.trim()
        && (form.city || form.custom_city_name.trim())) {
      try {
        const cityNameForGeo = form.city
          ? (selectedFormCity?.name || "")
          : form.custom_city_name.trim();
        const geo = await APIClientGeocodeAddress(form.address.trim(), cityNameForGeo);
        if (geo && geo.latitude != null && geo.longitude != null) {
          resolvedLat = geo.latitude;
          resolvedLng = geo.longitude;
          setForm(p => ({ ...p, latitude: resolvedLat, longitude: resolvedLng }));
        }
      } catch (_) { /* мягкая деградация: отправляем без координат, бэкенд подтвердит */ }
    }

    // 1. Собираем основные данные (без файлов вводных данных)
    const formData = new FormData();
    formData.append("phone", form.client_phone.trim());
    formData.append("last_name",  form.client_last_name.trim());
    formData.append("name",       form.client_name.trim());
    formData.append("patronymic", form.client_patronymic.trim());
    // Имя города для очистки адреса: либо из списка, либо введённое вручную
    const cityName = form.city
      ? (selectedFormCity?.name || "")
      : form.custom_city_name.trim();
    formData.append("address", stripCityFromAddress(form.address.trim(), cityName));

    if (form.area) formData.append("square", parseFloat(form.area));
    if (form.object_type) formData.append("object_type", form.object_type);
    formData.append("custom_city_name", form.city ? "" : form.custom_city_name.trim());
    if (!form.city && (isEdit || submittedApplicationRef.current)) formData.append("city", "");
    if (form.city) {
      formData.append("city", Number(form.city));
    }
    if (form.programs && form.programs.length > 0) {
      form.programs.forEach(pid => formData.append("programs", Number(pid)));
    }
    formData.append("tz", form.tz || "");
    formData.append("comment", form.comment || "");
    formData.append("contact_name", form.contact_name.trim());
    formData.append("contact_phone", form.contact_phone.trim());
    formData.append("contact_comment", form.contact_comment || "");
    for (const [field, value] of Object.entries(schedulePayload(form))) {
      formData.append(field, Array.isArray(value) ? JSON.stringify(value) : value ?? '');
    }
    if (form.plan_file && (isEdit || !draftPersistenceRef.current?.planWasSaved(form.plan_file))) formData.append("plan_file", form.plan_file);

    // ── Координаты (только если есть; иначе адрес подтвердит бэкенд по городу) ──
    if (resolvedLat != null) formData.append("latitude", resolvedLat);
    if (resolvedLng != null) formData.append("longitude", resolvedLng);

    // ── Желаемые даты (массив) ──

    try {
      ensureCurrent();
      // A lost confirmation response is resolved before starting another write.
      // This also keeps a subsequent, genuinely changed form from being ignored.
      if (editSaveRef.current.id && editSaveRef.current.phase === 'confirming') {
        const previous = await APIClientConfirmApplicationEdit(editOrder?.id || submittedApplicationRef.current?.id, editSaveRef.current.id);
        ensureCurrent();
        if (previous?.status !== 'ok') throw new Error(previous?.detail || 'Не получено подтверждение сохранения. Повторите действие.');
        editSaveRef.current = { id: null, phase: 'editing', files: new WeakMap() };
      }
      if (isEdit || submittedApplicationRef.current) {
        editSaveRef.current.id ||= applicationEditId();
        formData.append('edit_id', editSaveRef.current.id);
      }
      // 2. Отправка: черновик → финализация; редактирование → обновление; иначе — создание
      let res;
      if (submittedApplicationRef.current && !isEdit) {
        // Resume attachments on the same application; never create a second order.
        res = await APIClientUpdateApplication(submittedApplicationRef.current.id, formData);
        res = {...res, application_id: submittedApplicationRef.current.id,
          deal_pending: res?.deal_pending ?? submittedApplicationRef.current.deal_pending};
      } else if (submitDraftId && !isEdit) {
        res = await APIClientFinalizeDraft(submitDraftId, formData);
      } else if (isEdit) {
        res = await APIClientUpdateApplication(editOrder.id, formData);
      } else {
        res = await APIClientCreateApplication(formData);
      }

      ensureCurrent();
      if (res?.edit_id) editSaveRef.current.id = res.edit_id;
      if (res?.already_confirmed) {
        setSubmitted(true); setSubmitting(false); submitFlightRef.current = false;
        return;
      }

      if (res?.status === "error") {
        // Бэкенд вернул незаполненные обязательные поля — подсветим их.
        // Единая карта имён серверных полей → полей формы (+ контакты).
        const map = {
          phone: "client_phone", name: "client_name", address: "address",
          city: "city", square: "area",
          contact_name: "contact_name", contact_phone: "contact_phone",
        };
        const fieldErrors = {};
        // Финализация/обновление: missing: [{ field, label }]
        if (Array.isArray(res.missing) && res.missing.length) {
          res.missing.forEach(m => { fieldErrors[map[m.field] || m.field] = m.label; });
        }
        // Создание (DRF): { field: [msg], ... }
        if (res.fieldErrors && typeof res.fieldErrors === "object") {
          Object.entries(res.fieldErrors).forEach(([field, msgs]) => {
            const msg = Array.isArray(msgs) ? msgs[0] : String(msgs);
            fieldErrors[map[field] || field] = msg;
          });
        }
        if (Object.keys(fieldErrors).length) {
          setErrors(prev => ({ ...prev, ...fieldErrors }));
          scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        }
        setSubmitError(typeof res.detail === "string" ? res.detail : "Не удалось сохранить заявку.");
        setSubmitting(false); submitFlightRef.current = false;
        return;
      }

      const applicationId = isEdit
        ? editOrder.id
        : (res.application_id || res.id);

      // Защита от undefined
      if (!applicationId) {
        console.error("Не удалось получить ID заявки:", res);
        setSubmitError("Заявка создана, но не удалось загрузить файлы. Попробуйте отредактировать заявку.");
        setSubmitting(false); submitFlightRef.current = false;
        return;
      }

      if (!isEdit) {
        submittedApplicationRef.current = {id: applicationId, deal_pending: !!res?.deal_pending};
        draftIdRef.current = null; setDraftId(null);
      }
      // Remove only acknowledged work from the pending form, so retries continue it.
      for (const fileId of form.removed_file_ids) {
        if (!isEdit && draftPersistenceRef.current?.fileWasRemoved(fileId)) continue;
        const deleted = await APIClientDeleteApplicationFile(applicationId, fileId, editSaveRef.current.id);
        ensureCurrent();
        if (deleted?.status === 'error') throw new Error(deleted.detail || 'Не удалось удалить файл из заявки');
        setForm(prev => ({...prev, removed_file_ids: prev.removed_file_ids.filter(id => id !== fileId)}));
      }
      for (const file of form.input_files) {
        if (!isEdit && draftPersistenceRef.current?.fileReceipt(file)) continue;
        if (!editSaveRef.current.files.has(file)) editSaveRef.current.files.set(file,
          !isEdit ? draftPersistenceRef.current?.fileUploadId(file) || applicationEditId() : applicationEditId());
        const uploadRes = await APIClientUploadApplicationFile(applicationId, file,
          !isEdit && !editSaveRef.current.id, editSaveRef.current.id, editSaveRef.current.files.get(file));
        ensureCurrent();
        if (uploadRes?.status === "error" || !uploadRes?.id) {
          throw new Error(`Заявка №${applicationId} сохранена. Файл «${file.name}» не подтверждён: ${uploadRes?.detail || 'нет подтверждения сервера'}. Повторите отправку — новая заявка не создастся.`);
        }
        setForm(prev => ({...prev, input_files: prev.input_files.filter(item => item !== file),
          existing_files: [...prev.existing_files.filter(item => item.id !== uploadRes.id), uploadRes]}));
      }

      if (editSaveRef.current.id) {
        editSaveRef.current.phase = 'confirming';
        const confirmed = await APIClientConfirmApplicationEdit(applicationId, editSaveRef.current.id);
        ensureCurrent();
        if (confirmed?.status !== 'ok') throw new Error(confirmed?.detail || 'Изменения сохранены. Повторите подтверждение.');
        editSaveRef.current.phase = 'confirmed';
      }

      setDraftId(null);   // черновик стал заявкой
      setSubmittedPending(!!res?.deal_pending);
      setSubmitted(true);
    } catch (err) {
      if (saveScope === draftSessionRef.current) setSubmitError(err.message || "Произошла ошибка при сохранении. Попробуйте ещё раз.");
    }

    if (saveScope === draftSessionRef.current) { setSubmitting(false); submitFlightRef.current = false; }
  };

  const handleDetectCity = () => {
    if (!navigator.geolocation) {
      setDetectMsg("Геолокация недоступна в браузере");
      return;
    }
    setDetecting(true);
    setDetectMsg(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const res = await APIClientDetectCity({ latitude, longitude });
        setDetecting(false);
        if (res?.city_id) {
          // Город есть в справочнике — сохраняем FK + координаты
          setForm(p => ({ ...p, city: res.city_id, city_name: res.city_name || savedCityOption(res.city_id, '', citys)?.name || '', custom_city_name: "",
                          latitude: res.latitude ?? latitude, longitude: res.longitude ?? longitude }));
          setErrors(p => ({ ...p, city: "" }));
        } else if (res?.city_name) {
          // Определён, но нет в справочнике — ручной ввод
          setForm(p => ({ ...p, custom_city_name: res.city_name, city: null, city_name: '',
                          latitude: res.latitude ?? latitude, longitude: res.longitude ?? longitude }));
          setErrors(p => ({ ...p, city: "" }));
        } else {
          setDetectMsg(res?.message || "Не удалось определить город");
        }
      },
      () => {
        setDetecting(false);
        setDetectMsg("Не удалось получить геолокацию. Разрешите доступ к местоположению.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleClose = async () => {
    if (submitting || formRef.current?.files_preparing || formRef.current?.plan_preparing || closeFlightRef.current) return;
    if (!isEdit && !submitted && submittedApplicationRef.current
        && (formRef.current.input_files.length || formRef.current.removed_file_ids.length)) {
      setSubmitError('Заявка сохранена, но изменения файлов ещё не подтверждены. Повторите отправку или уберите неотправленные файлы перед закрытием.');
      return;
    }
    const closeScope = draftSessionRef.current;
    if (!isEdit && !submitted && !submittedApplicationRef.current && draftMeaningful(formRef.current)) {
      closeFlightRef.current = true;
      setClosing(true);
      setSubmitError('');
      try {
        await persistDraft(formRef.current);
        while (closeScope === draftSessionRef.current
            && !draftPersistenceRef.current.isPersisted(formRef.current)) {
          await persistDraft(formRef.current);
        }
      } catch (error) {
        if (closeScope !== draftSessionRef.current) return;
        setSubmitError(error.message || 'Черновик сохранён не полностью. Повторите сохранение перед закрытием.');
        setClosing(false);
        closeFlightRef.current = false;
        return;
      }
    }
    if (closeScope !== draftSessionRef.current) return;
    onClose();
    setTimeout(() => { setSubmitted(false); setSubmitError(""); }, 400);
  };

  if (open && !hasPermission(requiredPerm)) {
    // Автоматически закрываем, если нет прав
    onClose();
    return null;
  }

  return (
    <AnimatePresence>
      {open && (
        <Modal
          open={open}
          // onClose={handleClose}
          sx={{ backdropFilter: "blur(8px)", alignItems: "flex-end" }}
          slotProps={{
            backdrop: {
              component: motion.div,
              initial: { opacity: 0 },
              animate: { opacity: 1 },
              exit: { opacity: 0 },
              transition: { duration: 0.28 },
            }
          }}
        >
          <ModalOverflow sx={{ height: "auto", p: { xs: 0, sm: 2 } }}>
            <ModalDialog
              layout="center"
              component={motion.div}
              initial={{ y: "100%", opacity: 0.6 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0.6 }}
              transition={{ duration: 0.42, ease: [0.32, 0.72, 0, 1] }}
              sx={{
                maxWidth: 680,
                width: "100%",
                minHeight: { xs: '100dvh', sm: 200 },
                gap: 0,
                borderRadius: { xs: 0, sm: "28px" },
                p: 0,
                boxShadow: "0 32px 96px rgba(0,0,0,0.22), 0 0 0 1px rgba(99,102,241,0.08)",
                overflow: "visible",
                border: "1.5px solid",
                borderColor: "var(--joy-palette-neutral-100)",
                position: "relative",
              }}
            >
              {/* Header */}
              <Stack
                direction="row" alignItems="center" justifyContent="space-between"
                sx={{ px: 2, py: 2, borderBottom: "1px solid", borderColor: "divider", flexShrink: 0 }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Sheet
                    variant="solid" color="primary"
                    sx={{
                      width: 42, height: 42, borderRadius: "12px",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "1.15rem",
                      background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                      boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
                    }}
                  >
                    <LocalOfferRoundedIcon/>
                  </Sheet>
                  <Box>
                    <Stack direction={'row'} justifyContent={'space-between'} sx={{ width: '100%' }}>
                      <Typography level="title-md" fontWeight={800}>
                        {isEdit ? `Редактирование заявки №${editOrder.id}` : "Новая заявка"}
                      </Typography>
                      {!isEdit && !submitted && !submittedApplicationRef.current && draftState.status !== 'idle' && (
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 2, flexShrink: 0 }}>
                          {draftState.status === 'saving' && <CircularProgress size="sm" />}
                          <Typography level="body-xs" color="neutral">
                            {draftState.status === 'saving' && 'Сохранение черновика…'}
                            {draftState.status === 'saved' && `Черновик сохранён${draftState.at ? ' · ' + draftState.at.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : ''}`}
                            {draftState.status === 'error' && 'Черновик не сохранён'}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                    <Typography level="body-xs" color="neutral">Заполните информацию об объекте</Typography>
                  </Box>
                </Stack>
                <IconButton
                  variant="soft" color="neutral" size="sm"
                  onClick={handleClose}
                  disabled={submitting || closing || form.files_preparing || form.plan_preparing}
                  aria-label="Сохранить черновик и закрыть"
                  sx={{ borderRadius: "50%" }}
                >
                  <CloseRoundedIcon />
                </IconButton>
              </Stack>

              {/* Body */}
              <Box ref={scrollRef} sx={{ overflowY: "auto", flex: 1 }}>
                <AnimatePresence mode="wait">
                  {submitted ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.88, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
                    >
                      <Stack alignItems="center" justifyContent="center" spacing={2.5} sx={{ py: 8, px: 4, textAlign: "center" }}>
                        <motion.div
                          initial={{ scale: 0 }} animate={{ scale: 1 }}
                          transition={{ delay: 0.15, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
                        >
                          <Sheet
                            variant="soft" color="success"
                            sx={{ width: 80, height: 80, borderRadius: "24px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.5rem" }}
                          >
                            <CheckCircleRoundedIcon sx={{ fontSize: 48, color: "success.500" }} />
                          </Sheet>
                        </motion.div>
                        <Box>
                          <Typography level="h3" fontWeight={800}>
                            {isEdit ? "Изменения сохранены" : "Заявка отправлена!"}
                          </Typography>
                          <Typography level="body-sm" color="neutral" sx={{ mt: 1, maxWidth: 320 }}>
                            {submittedPending
                              ? "Заявка принята и уже у менеджеров. Сейчас оформляем её в системе — статус обновится автоматически, повторно отправлять не нужно."
                              : "Мы свяжемся с вами в течение 5 минут для уточнения деталей"}
                          </Typography>
                        </Box>
                        <Button variant="soft" color="neutral" onClick={handleClose} sx={{ mt: 1 }}>
                          Закрыть
                        </Button>
                      </Stack>
                    </motion.div>
                  ) : (
                    <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <Box component="fieldset" disabled={submitting || closing} sx={{ p: 2, m: 0, border: 0, minWidth: 0 }}>
                        <Section title="Данные заказчика" icon={<AssignmentIndRoundedIcon sx={{ fontSize: 18 }} />} index={0}>
                          <FormControl error={!!errors.client_last_name}>
                            <FormLabel>Фамилия <Typography component="span" color="danger">*</Typography></FormLabel>
                            <Input value={form.client_last_name} onChange={e => set("client_last_name", e.target.value)} placeholder="Иванов" />
                            {errors.client_last_name && <FormHelperText>{errors.client_last_name}</FormHelperText>}
                          </FormControl>
                          <FormControl error={!!errors.client_name}>
                            <FormLabel>Имя <Typography component="span" color="danger">*</Typography></FormLabel>
                            <Input value={form.client_name} onChange={e => set("client_name", e.target.value)} placeholder="Алексей" />
                            {errors.client_name && <FormHelperText>{errors.client_name}</FormHelperText>}
                          </FormControl>
                          <FormControl error={!!errors.client_patronymic}>
                            <FormLabel>Отчество <Typography component="span" level="body-xs" color="neutral">(необязательно)</Typography></FormLabel>
                            <Input value={form.client_patronymic} onChange={e => set("client_patronymic", e.target.value)} placeholder="Сергеевич" />
                          </FormControl>
                          <FormControl error={!!errors.client_phone}>
                            <FormLabel>Телефон <Typography component="span" color="danger">*</Typography></FormLabel>
                            <MaskedInput
                              mask="+7 (000) 000-00-00" placeholder="+7 (000) 000-00-00" value={form.client_phone} 
                              onChange={e => set("client_phone", e.target.value)}
                            />
                            {errors.client_phone && <FormHelperText>{errors.client_phone}</FormHelperText>}
                          </FormControl>
                        </Section>

                        <Divider sx={{ my: 2 }} />

                        <Section title="Информация об объекте" icon={<HomeWorkRoundedIcon sx={{ fontSize: 18 }} />} index={1}>
                          <FormControl error={!!errors.city}>
                            <FormLabel>Город <Typography component="span" color="danger">*</Typography></FormLabel>
                            <CityAutocomplete
                              value={form.city}
                              cityName={form.city_name}
                              knownCities={citys}
                              customCityName={form.custom_city_name}
                              onChange={(v, option) => {
                                setForm(p => ({
                                  ...p,
                                  city: v,
                                  city_name: option?.name || '',
                                  custom_city_name: "",
                                  address: "",
                                  latitude: null,
                                  longitude: null,
                                }));
                                setErrors(p => ({ ...p, city: "", address: "" }));
                              }}
                              onCustomChange={(name) => {
                                setForm(p => ({
                                  ...p,
                                  custom_city_name: name,
                                  city: null,
                                  city_name: '',
                                  // При смене города руками — сбрасываем адрес
                                  address: name !== p.custom_city_name ? "" : p.address,
                                  latitude: name !== p.custom_city_name ? null : p.latitude,
                                  longitude: name !== p.custom_city_name ? null : p.longitude,
                                }));
                                setErrors(p => ({ ...p, city: "" }));
                              }}
                              error={errors.city}
                            />
                            {errors.city && <FormHelperText>{errors.city}</FormHelperText>}
                          </FormControl>

                          <Button
                            size="sm" variant="soft" color="neutral"
                            startDecorator={<MyLocationRoundedIcon sx={{ fontSize: 16 }} />}
                            loading={detecting}
                            onClick={handleDetectCity}
                            sx={{ alignSelf: "end", justifySelf: "end", width: "fit-content", minHeight: 36 }}
                          >
                            Определить город
                          </Button>
                          {detectMsg && (
                            <Typography level="body-xs" color="warning" sx={{ mt: 0.5 }}>
                              {detectMsg}
                            </Typography>
                          )}

                          {availability.data && (
                            <Sheet
                              variant="soft"
                              color={availability.data.in_city ? "success" : (availability.data.nearest ? "warning" : "neutral")}
                              sx={{ gridColumn: "1 / -1", borderRadius: "10px", p: 1.25 }}
                            >
                              <Typography level="body-xs">{availability.data.message}</Typography>
                              <RouteAttribution route={availability.data.nearest?.route}/>
                            </Sheet>
                          )}

                          <Box sx={{ gridColumn: "1 / -1" }}>
                            <FormControl error={!!errors.address}>
                              <FormLabel>Адрес объекта <Typography component="span" color="danger">*</Typography></FormLabel>
                              <Input
                                value={form.address}
                                onFocus={() => setAddressFocused(true)}
                                onBlur={() => setAddressFocused(false)}
                                onChange={e => {
                                  const newValue = e.target.value;
                                  // Если пользователь правит адрес руками — координаты больше не валидны
                                  setForm(p => ({
                                    ...p,
                                    address: newValue,
                                    latitude: null,
                                    longitude: null,
                                  }));
                                  setErrors(p => ({ ...p, address: "" }));
                                }}
                                placeholder="ул. Ленина, 42"
                                endDecorator={
                                  form.latitude && form.longitude
                                    ? <CheckCircleRoundedIcon sx={{ fontSize: 18, color: "success.500" }} />
                                    : <Typography level="body-xs" color="neutral">можно уточнить на карте</Typography>
                                }
                              />
                              {errors.address && <FormHelperText>{errors.address}</FormHelperText>}
                            </FormControl>

                            <AddressMap
                              addressFocused={addressFocused}
                              address={form.address}
                              cityName={
                                form.city
                                  ? (selectedFormCity?.name || "")
                                  : form.custom_city_name
                              }
                              disabled={!form.city && !form.custom_city_name.trim()}
                              lat={form.latitude}
                              lng={form.longitude}
                              onLocationChange={(lat, lng) => {
                                setForm(p => ({ ...p, latitude: lat, longitude: lng }));
                              }}
                              onAddressChange={(newAddress) => {
                                // Адрес пришёл из карты/подсказки → НЕ сбрасываем координаты
                                const cityName = form.city
                                  ? (selectedFormCity?.name || "")
                                  : form.custom_city_name;
                                setForm(p => ({ ...p, address: stripCityFromAddress(newAddress, cityName) }));
                                setErrors(p => ({ ...p, address: "" }));
                              }}
                            />
                          </Box>
                          <FormControl error={!!errors.object_type}>
                            <FormLabel>Тип объекта <Typography component="span" color="danger">*</Typography></FormLabel>
                            <Select
                              value={form.object_type || null}
                              onChange={(_, v) => set("object_type", v)}
                              placeholder="Выберите тип"
                              color={errors.object_type ? "danger" : "neutral"}
                            >
                              {[...new Set([...OBJECT_TYPES, form.object_type].filter(Boolean))].map(o => <Option key={o} value={o}>{o}</Option>)}
                            </Select>
                            {errors.object_type && <FormHelperText>{errors.object_type}</FormHelperText>}
                          </FormControl>
                          <FormControl error={!!errors.area}>
                            <FormLabel>Площадь (м²) <Typography component="span" color="danger">*</Typography></FormLabel>
                            <Input
                              value={form.area}
                              onChange={e => set("area", e.target.value.replace(/\D/g, ""))}
                              placeholder="100"
                              slotProps={{ input: { inputMode: "numeric" } }}
                              endDecorator={<Typography level="body-sm" color="neutral">м²</Typography>}
                            />
                            {errors.area && <FormHelperText>{errors.area}</FormHelperText>}
                          </FormControl>

                          {(estimate.total != null || estimate.loading || estimate.error) && (
                            <Sheet variant="soft" color="primary" sx={{ mt: 1.5, borderRadius: "12px", p: 1.5 }}>
                              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <LocalOfferRoundedIcon sx={{ fontSize: 18 }} />
                                  <Typography level="body-sm" fontWeight={600}>{estimate.incomplete ? 'Стоимость работ без выезда' : 'Предполагаемая стоимость'}</Typography>
                                </Stack>
                                {estimate.loading
                                  ? <CircularProgress size="sm" />
                                  : estimate.total != null ? <Typography level="title-md" fontWeight={800}>
                                      {Number(estimate.total).toLocaleString("ru-RU")} ₽
                                    </Typography> : null}
                              </Stack>
                              {estimate.tariffName && !estimate.loading && <Typography level="body-xs" sx={{mt: 0.5}}>Тариф компании: {estimate.tariffName}</Typography>}
                              {estimate.incomplete && !estimate.loading && <Typography level="body-xs" color="warning" sx={{mt: 0.5}}>
                                Стоимость выезда уточнит менеджер. {estimate.route?.message || ''}
                              </Typography>}
                              {(estimate.incomplete || estimate.error) && !estimate.loading && <Button size="sm" variant="plain" sx={{mt: 0.5}}
                                onClick={() => setQuoteRevision(value => value + 1)}>Повторить расчёт</Button>}
                              {estimate.error && <Typography level="body-xs" color="danger" role="alert">{estimate.error}</Typography>}
                              <RouteAttribution route={estimate.route}/>
                              {estimate.minimumApplied && !estimate.loading && (
                                <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                  Применена минимальная стоимость работ
                                </Typography>
                              )}
                              <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                Ориентировочно; финальную сумму подтвердит менеджер.
                              </Typography>
                            </Sheet>
                          )}
                        </Section>

                        <Divider sx={{ my: 2 }} />

                        <Section title="Параметры работы" icon={<ArchitectureRoundedIcon sx={{ fontSize: 18 }} />} index={2}>
                          <FormControl error={!!errors.programs}>
                            <FormLabel>Программы для отрисовки <Typography component="span" color="danger">*</Typography></FormLabel>
                            <Select
                              multiple
                              value={form.programs}
                              onChange={(_, v) => set("programs", v)}
                              placeholder="Выберите программы"
                              color={errors.programs ? "danger" : "neutral"}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', gap: '0.25rem', flexWrap: "wrap" }}>
                                  {selected.map((selectedOption) => (
                                    <Chip variant="soft" color="primary">
                                      {selectedOption.label}
                                    </Chip>
                                  ))}
                                </Box>
                              )}
                            >
                              {programs.map(p => (
                                  <Option key={p.id} value={p.id}>{p.name}</Option>
                              ))}
                            </Select>
                            {errors.programs && <FormHelperText>{errors.programs}</FormHelperText>}
                          </FormControl>
                          <FormControl>
                            <FormLabel>
                              План помещения{" "}
                              <Typography level="body-xs" color="neutral">(JPG, PNG, PDF)</Typography>
                            </FormLabel>
                            <Sheet
                              component="label"
                              variant="outlined"
                              sx={{
                                borderRadius: "md", borderStyle: "dashed", p: 1.5,
                                cursor: "pointer", display: "flex", alignItems: "center", gap: 1,
                                transition: "border-color 0.2s, background 0.2s",
                                "&:hover": { borderColor: "primary.400", background: "var(--joy-palette-primary-50)" },
                              }}
                            >
                              <AttachFileRoundedIcon sx={{ fontSize: 18, color: "text.tertiary" }} />
                              <Typography level="body-sm" color="neutral">
                                {form.plan_file ? form.plan_file.name : form.existing_plan_file ? "Заменить план помещения" : "Прикрепить файл"}
                              </Typography>
                              <input
                                type="file" accept=".jpg,.jpeg,.png,.pdf,.heic,.heif,image/heic,image/heif" style={{ display: "none" }}
                                onChange={async e => {
                                    const f = e.target.files[0];
                                    e.target.value = '';
                                    if (!f) return;
                                    const session = draftSessionRef.current;
                                    setForm(previous => ({ ...previous, plan_preparing: true }));
                                    try {
                                      const converted = await heicToJpeg(f);
                                      if (session === draftSessionRef.current) set('plan_file', converted);
                                    } catch (error) {
                                      if (session === draftSessionRef.current) setSubmitError(error.message || 'Не удалось подготовить план помещения');
                                    } finally {
                                      if (session === draftSessionRef.current) setForm(previous => ({ ...previous, plan_preparing: false }));
                                    }
                                }}
                              />
                            </Sheet>
                            {form.existing_plan_file && (
                              <Link
                                href={/^https?:\/\//i.test(form.existing_plan_file) ? form.existing_plan_file : `${API_URL}${form.existing_plan_file}`}
                                target="_blank" rel="noopener noreferrer" level="body-sm" sx={{ mt: 1 }}
                              >
                                Открыть сохранённый план помещения
                              </Link>
                            )}
                          </FormControl>
                        </Section>

                        <Divider sx={{ my: 2 }} />

                        <Section title="Когда нужна услуга" icon={<AccessTimeRoundedIcon sx={{ fontSize: 18 }} />} index={3}>
                          <ApplicationScheduleFields form={form} onChange={setSchedule} options={DESIRED_TIME_OPTIONS}
                            error={errors.desired_slots} renderCalendar={props => <MultiDateCalendar {...props}/>} />
                        </Section>

                        <Divider sx={{ my: 2 }} />

                        <Section title="Дополнительная информация" icon={<FeedRoundedIcon sx={{ fontSize: 18 }} />} index={4}>
                          <Box sx={{ gridColumn: "1 / -1" }}>
                            <FormControl>
                              <FormLabel>Цель замера, индивидуальные требования</FormLabel>

                              {/* Подсказка: что входит в базовый обмер */}
                              {(baseSpec?.body || baseSpec?.file_url) && (
                                <Box sx={{
                                  mb: 1, p: 1.25, borderRadius: '10px',
                                  bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100',
                                }}>
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                      {baseSpec?.title || 'Базовое ТЗ на обмеры'} — что входит в стандартный обмер.
                                    </Typography>
                                    {baseSpec?.file_url && (
                                      <Typography level="body-xs" component="a" href={baseSpec.file_url} target="_blank" rel="noreferrer"
                                        sx={{ fontWeight: 700, color: 'primary.600', textDecoration: 'none' }}>
                                        Открыть PDF
                                      </Typography>
                                    )}
                                    {baseSpec?.body && (
                                      <Typography level="body-xs" sx={{ fontWeight: 700, cursor: 'pointer', color: 'success.600' }}
                                        onClick={() => set("tz", form.tz ? `${form.tz}\n\n${baseSpec.body}` : baseSpec.body)}>
                                        Подставить в требования
                                      </Typography>
                                    )}
                                  </Stack>
                                </Box>
                              )}

                              <Textarea
                                value={form.tz}
                                onChange={e => set("tz", e.target.value)}
                                placeholder="Требования к чертежу, стандарты, формат сдачи..."
                                minRows={3}
                              />
                            </FormControl>
                          </Box>

                          {/* ── НОВОЕ: Зона загрузки файлов ── */}
                          <Box sx={{ gridColumn: "1 / -1" }}>
                            <FormControl>
                              <FormLabel>Вводные данные</FormLabel>
                              <FormHelperText sx={{mt: 0, mb: 1}}>Файлы ТЗ, фотографии с объекта и важная информация, которая поможет выполнить замер.</FormHelperText>
                              <FileDropZone
                                files={form.input_files}
                                setFiles={(updater) => {
                                  setForm(prev => ({
                                    ...prev,
                                    input_files: typeof updater === 'function' ? updater(prev.input_files) : updater,
                                  }));
                                }}
                                existingFiles={form.existing_files}
                                disabled={submitting || closing || form.plan_preparing || (!isEdit && draftState.status === 'saving')}
                                onPreparingChange={preparing => setForm(previous => ({ ...previous, files_preparing: preparing }))}
                                onRemoveExisting={handleRemoveExistingFile}
                              />
                            </FormControl>
                          </Box>

                          <Box sx={{ gridColumn: "1 / -1" }}>
                            <FormControl>
                              <FormLabel>Комментарий</FormLabel>
                              <Textarea
                                value={form.comment}
                                onChange={e => set("comment", e.target.value)}
                                placeholder="Любая дополнительная информация об объекте..."
                                minRows={2}
                              />
                            </FormControl>
                          </Box>
                        </Section>

                        <Divider sx={{ my: 2 }} />

                        <Section title="Контакт на объекте" icon={<ContactPhoneRoundedIcon sx={{ fontSize: 18 }} />} index={4}>
                          <FormControl error={!!errors.contact_name}>
                            <FormLabel>Имя встречающего <Typography component="span" color="danger">*</Typography></FormLabel>
                            <Input value={form.contact_name} onChange={e => set("contact_name", e.target.value)} placeholder="Кто откроет дверь?" />
                            {errors.contact_name && <FormHelperText>{errors.contact_name}</FormHelperText>}
                          </FormControl>
                          <FormControl error={!!errors.contact_phone}>
                            <FormLabel>Телефон встречающего <Typography component="span" color="danger">*</Typography></FormLabel>
                            <MaskedInput
                              mask="+7 (000) 000-00-00" placeholder="+7 (000) 000-00-00" value={form.contact_phone}
                              onChange={e => set("contact_phone", e.target.value)}
                            />
                            {errors.contact_phone && <FormHelperText>{errors.contact_phone}</FormHelperText>}
                          </FormControl>
                          <Box sx={{ gridColumn: "1 / -1" }}>
                            <FormControl>
                              <FormLabel>Комментарий</FormLabel>
                              <Input value={form.contact_comment} onChange={e => set("contact_comment", e.target.value)} placeholder="Код домофона, этаж, как найти..." />
                            </FormControl>
                          </Box>
                        </Section>

                        {/* Ошибка отправки */}
                        <AnimatePresence>
                          {submitError && (
                            <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                              <Sheet variant="soft" color="danger" sx={{ borderRadius: "10px", p: 1.5, mt: 1 }}>
                                <Typography level="body-sm" color="danger">{submitError}</Typography>
                              </Sheet>
                            </motion.div>
                          )}
                        </AnimatePresence>

                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>

              {/* Footer */}
              {!submitted && (
                <Stack
                  direction="row" spacing={1.5}
                  sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider", flexShrink: 0 }}
                >
                  <motion.div style={{ flex: 1 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      variant="solid" color="primary" onClick={handleSubmit} loading={submitting}
                      disabled={closing || form.files_preparing || form.plan_preparing}
                      sx={{
                        width: "100%", fontWeight: 700, borderRadius: "12px",
                        background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                        boxShadow: "0 4px 16px rgba(99,102,241,0.35)",
                      }}
                    >
                      {isEdit ? "Сохранить изменения" : "Отправить заявку"}
                    </Button>
                  </motion.div>
                </Stack>
              )}
            </ModalDialog>
          </ModalOverflow>
        </Modal>
      )}
    </AnimatePresence>
  );
}
